/**
 * videoDocsService — accesso dati della documentazione video di piattaforma.
 *
 * Pattern console: letture/scritture Firestore DIRETTE (la console è solo
 * super_admin, le security rules bloccano chiunque altro), come
 * platformCrmService. I file MP4 vanno su Storage con `uploadBytesResumable`
 * (progresso in UI) sotto `platform_docs_videos/{video_id}/{file}`.
 *
 * Doc canonico: docs/01-ARCHITECTURE/VIDEO-DOCS-MODEL.md
 */

import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable, type UploadTask } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import type {
  PlatformDocsChapter,
  PlatformDocsChapterWriteInput,
  PlatformDocsVideo,
  PlatformDocsVideoWriteInput,
} from '../types/videoDocs';

const CHAPTERS_COLLECTION = 'platform_docs_chapters';
const VIDEOS_COLLECTION = 'platform_docs_videos';
/** Radice Storage dei file video (stesso nome della collezione, per simmetria). */
const STORAGE_ROOT = 'platform_docs_videos';

// ============================================================================
// MAPPING — regola progetto: mai whitelist, sempre spread + override id
// ============================================================================

function mapSnap<T>(d: QueryDocumentSnapshot<DocumentData>): T {
  return { ...(d.data() as Record<string, unknown>), id: d.id } as T;
}

/** Rimuove le chiavi `undefined` (Firestore rifiuta i valori undefined). */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

// ============================================================================
// CAPITOLI
// ============================================================================

/** Carica tutti i capitoli ordinati per `ordine` (poi titolo a parità). */
export async function listChapters(): Promise<PlatformDocsChapter[]> {
  const snap = await getDocs(collection(db, CHAPTERS_COLLECTION));
  const chapters = snap.docs.map((d) => mapSnap<PlatformDocsChapter>(d));
  chapters.sort(
    (a, b) =>
      (a.ordine ?? 0) - (b.ordine ?? 0) ||
      (a.titolo ?? '').localeCompare(b.titolo ?? '', 'it', { sensitivity: 'base' }),
  );
  return chapters;
}

/** Crea un capitolo. Restituisce l'id del documento creato. */
export async function createChapter(input: PlatformDocsChapterWriteInput): Promise<string> {
  const ref_ = await addDoc(collection(db, CHAPTERS_COLLECTION), stripUndefined({ ...input }));
  return ref_.id;
}

/** Aggiorna i campi editabili di un capitolo (rinomina, attiva/disattiva…). */
export async function updateChapter(
  id: string,
  patch: Partial<PlatformDocsChapterWriteInput>,
): Promise<void> {
  await updateDoc(doc(db, CHAPTERS_COLLECTION, id), stripUndefined({ ...patch }));
}

/**
 * Riscrive `ordine` = indice per la sequenza di capitoli passata (batch unico).
 * Normalizza anche eventuali buchi/duplicati storici: l'ordine risultante è
 * sempre 0..n-1 coerente con ciò che l'operatore vede a schermo.
 */
export async function reorderChapters(orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    batch.update(doc(db, CHAPTERS_COLLECTION, id), { ordine: index });
  });
  await batch.commit();
}

// ============================================================================
// VIDEO — metadati Firestore
// ============================================================================

/** Carica tutti i video, ordinati per `ordine` (poi titolo a parità). */
export async function listVideos(): Promise<PlatformDocsVideo[]> {
  const snap = await getDocs(collection(db, VIDEOS_COLLECTION));
  const videos = snap.docs.map((d) => mapSnap<PlatformDocsVideo>(d));
  videos.sort(
    (a, b) =>
      (a.ordine ?? 0) - (b.ordine ?? 0) ||
      (a.titolo ?? '').localeCompare(b.titolo ?? '', 'it', { sensitivity: 'base' }),
  );
  return videos;
}

/**
 * Genera un id documento nuovo SENZA scrivere nulla: serve PRIMA dell'upload
 * per costruire il percorso Storage `platform_docs_videos/{video_id}/{file}`.
 */
export function newVideoDocId(): string {
  return doc(collection(db, VIDEOS_COLLECTION)).id;
}

/** Crea il documento metadati di un video (id pre-generato con newVideoDocId). */
export async function createVideo(id: string, input: PlatformDocsVideoWriteInput): Promise<void> {
  await setDoc(doc(db, VIDEOS_COLLECTION, id), stripUndefined({
    ...input,
    created_at: serverTimestamp(),
    created_by: auth.currentUser?.uid ?? null,
    updated_at: serverTimestamp(),
  }));
}

/** Aggiorna i metadati di un video esistente (updateDoc diretto, pattern console). */
export async function updateVideo(
  id: string,
  patch: Partial<PlatformDocsVideoWriteInput>,
): Promise<void> {
  await updateDoc(doc(db, VIDEOS_COLLECTION, id), stripUndefined({
    ...patch,
    updated_at: serverTimestamp(),
  }));
}

// ============================================================================
// VIDEO — file su Storage
// ============================================================================

/**
 * Nome file sicuro per Storage: mantiene lettere/numeri/punto/trattino,
 * il resto diventa underscore (niente spazi o caratteri esotici nei path).
 */
export function sanitizeFileName(name: string): string {
  return name.replace(/[^\w.\-]+/g, '_');
}

/** Percorso Storage canonico di un file video. */
export function buildVideoStoragePath(videoId: string, fileName: string): string {
  return `${STORAGE_ROOT}/${videoId}/${sanitizeFileName(fileName)}`;
}

/**
 * Avvia l'upload resumable del file MP4. Il chiamante osserva il progresso
 * con `task.on('state_changed', …)` e può annullare con `task.cancel()`.
 */
export function uploadVideoFile(videoId: string, file: File): { task: UploadTask; storagePath: string } {
  const storagePath = buildVideoStoragePath(videoId, file.name);
  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: file.type || 'video/mp4',
  });
  return { task, storagePath };
}

/** URL firmato di download/streaming per l'anteprima inline nella console. */
export async function getVideoDownloadUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}

// ============================================================================
// HELPER DI DOMINIO (puri, senza I/O)
// ============================================================================

/**
 * Valori distinti di `aree_coperte` presenti nei video caricati, ordinati
 * alfabeticamente (it). Alimenta l'autocomplete SENZA hardcode: le aree sono
 * valori nei dati, mai costanti nel codice.
 */
export function distinctAreeCoperte(videos: PlatformDocsVideo[]): string[] {
  const values = new Set<string>();
  for (const video of videos) {
    for (const area of video.aree_coperte ?? []) {
      const value = area.trim();
      if (value) values.add(value);
    }
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
}

/**
 * Versione app suggerita per un nuovo video: "AAAA.MM" del mese corrente,
 * CALCOLATA (mai hardcoded). Il campo resta testo libero: l'operatore può
 * sovrascriverla.
 */
export function suggestedVersioneApp(now: Date = new Date()): string {
  return `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Rileva la durata (secondi interi) di un file video SENZA toccare il DOM
 * visibile: crea un elemento <video> off-DOM con un blob URL e legge
 * `duration` da loadedmetadata. Restituisce null se il browser non riesce a
 * leggere i metadati (campo lasciato vuoto, mai un valore inventato).
 */
export function detectVideoDurationSeconds(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const seconds = video.duration;
      cleanup();
      resolve(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = url;
  });
}
